import { User, Calendar, UserPlus, ArrowUpRight, Pencil } from 'lucide-react';
import {
    getUserImageUrl,
    getUserBannerUrl,
    getMolduraImageUrl,
    getTagImageUrl,
    DEFAULT_AVATAR,
    perfilAvatarShapeClass,
} from '@/utils/image-utils';
import { LevelBadge } from '@/components/ranking/LevelBadge';
import { XpProgressBar } from '@/components/ranking/XpProgressBar';
import { calculateProgress } from '@/utils/utils';
import { UserTagBadge } from '@/components/user/UserTagBadge';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

function formatMemberSinceAppStyle(iso: string | undefined): string {
    if (!iso) return '';
    const d = dayjs(iso);
    if (!d.isValid()) return '';
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `Membro desde ${months[d.month()]} ${d.year()}`;
}

function xpRangeForLevel(level: number): { xpStart: number; xpEnd: number } {
    const lv = Number.isFinite(level) && level >= 1 ? Math.floor(level) : 1;
    const xpStart = lv <= 1 ? 0 : Math.floor(50 * Math.pow(lv, 1.6));
    const xpEnd = Math.floor(50 * Math.pow(lv + 1, 1.6));
    return { xpStart, xpEnd };
}

interface PerfilHeaderStats {
    total_leituras: number;
    total_lidos: number;
    total_favoritos: number;
    total_comentarios: number;
    total_lendo: number;
}

export type PerfilRankingPosicao = {
    posicao_global: number;
    posicao_semanal: number | null;
};

/** Seguir / deixar de seguir no perfil público (`POST usuarios/:id/seguir`). */
export type PerfilFollowActions = {
    canToggle: boolean;
    isFollowing: boolean;
    loading: boolean;
    onToggle: () => void | Promise<void>;
};

interface PerfilHeaderProps {
    user: any;
    isOwner?: boolean;
    onEdit?: () => void;
    onLogout?: () => void;
    stats?: PerfilHeaderStats;
    perfilStyle?: 'default' | 'perfil2';
    /** Posição em `xp/ranking/posicao` (global + semanal corrente). */
    rankingPosicao?: PerfilRankingPosicao | null;
    /** Só no perfil de outrem: botão seguir + estado. */
    followActions?: PerfilFollowActions | null;
}

function SeguidoresSeguindoRow({
    seguidores,
    seguindo,
    className = '',
}: {
    seguidores: number;
    seguindo: number;
    className?: string;
}) {
    return (
        <div
            className={`flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-white/55 tabular-nums md:justify-start md:gap-x-10 lg:gap-x-12 ${className}`}
        >
            <span>
                <span className="font-semibold text-white">{seguidores}</span> seguidores
            </span>
            <span className="text-white/25 hidden sm:inline">·</span>
            <span>
                <span className="font-semibold text-white">{seguindo}</span> seguindo
            </span>
        </div>
    );
}

export function PerfilHeader({
    user,
    isOwner = false,
    onEdit,
    onLogout,
    stats,
    perfilStyle = 'default',
    rankingPosicao = null,
    followActions = null,
}: PerfilHeaderProps) {
    const ownerAvatarShape = perfilAvatarShapeClass(user?.moldura?.mol_tipo, perfilStyle);

    if (isOwner) {
        const ownerLevel = user.usr_nivel ?? 1;
        const ownerXp = user.usr_xp ?? 0;
        const { xpStart, xpEnd } = xpRangeForLevel(ownerLevel);
        const xpInLevel = Math.max(0, ownerXp - xpStart);
        const xpNeedLevel = Math.max(0, xpEnd - xpStart);
        const plan = user?.plan as
            | { name?: string; nome?: string; isActive?: boolean; tag_imagem?: string | null }
            | undefined;
        const planName = (plan?.nome ?? plan?.name ?? '').trim();
        const planTagUrl = getTagImageUrl(plan?.tag_imagem ?? null);
        const nickStr = (user.usr_nick || 'usuario').trim();
        const nomeStr = (user.usr_nome || '').trim();
        const showSecondaryName = nomeStr.length > 0 && nomeStr.toLowerCase() !== nickStr.toLowerCase();
        const memberSinceLabel = formatMemberSinceAppStyle(user.usr_criado_em);

        // MeuPerfilPage layout
        const rankingBadgeClass = "inline-flex items-center px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[11px] font-semibold text-white/75 hover:text-orange-400 hover:border-orange-500/25 transition-colors";

        return (
            <div className="bg-transparent rounded-none md:rounded-2xl overflow-hidden mb-6 md:mb-8">
                {/* Banner */}
                <div className="relative h-36 sm:h-40 md:h-52 bg-neutral-800/50">
                    {user.usr_banner ? (
                        <img
                            src={getUserBannerUrl(user.usr_id, user.usr_banner) || ''}
                            alt="Banner"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-neutral-800/50 to-neutral-900/50" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/50 to-transparent" />
                </div>

                <div className="px-4 md:px-6 pb-6 -mt-10 md:-mt-16 relative z-10">
                    <div className="flex flex-col md:flex-row items-center md:items-end gap-4 md:gap-8 lg:gap-12">
                        {/* Avatar com Moldura */}
                        <div className="relative shrink-0">
                            {user.moldura && (
                                <div className="absolute -inset-1 z-20 pointer-events-none">
                                    <img
                                        src={getMolduraImageUrl(user.moldura.mol_imagem) || ''}
                                        className="w-[100%] h-[100%] max-w-none max-h-none -left-[20%] -top-[20%]"
                                        alt=""
                                    />
                                </div>
                            )}
                            <div className={`w-24 h-24 md:w-32 md:h-32 ${ownerAvatarShape} overflow-hidden border-3 border-neutral-950 bg-neutral-800 shadow-xl relative z-10`}>
                                <img
                                    src={getUserImageUrl(user.usr_id, user.usr_imagem)}
                                    alt={user.usr_nome}
                                    className="w-full h-full object-cover"
                                    onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_AVATAR; }}
                                />
                            </div>
                            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-30">
                                <LevelBadge level={user.usr_nivel || 1} size="md" className="shadow-black/50" />
                            </div>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0 text-center md:text-left w-full md:w-auto pb-1">

                            {/* Linha superior: nick + VIP — ranking à direita no desktop */}
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between md:gap-4">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                                        <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-1.5">
                                            <span className="text-orange-400">@</span>
                                            <span className="truncate">{nickStr}</span>
                                        </h1>
                                        {user?.is_vip && (planName || planTagUrl) ? (
                                            <span className="inline-flex items-center gap-1 max-w-[min(100%,200px)] px-1.5 py-0.5 rounded-md border border-white/20 bg-white/10 text-[10px] sm:text-[11px] font-semibold text-white leading-tight shrink-0">
                                                {planTagUrl ? (
                                                    <img src={planTagUrl} alt="" className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-sm object-contain shrink-0 bg-black/10" />
                                                ) : null}
                                                <span className="truncate">{planName || 'VIP'}</span>
                                            </span>
                                        ) : user?.is_vip ? (
                                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-orange-500/20 text-orange-400 border border-orange-500/30 tracking-wider shrink-0">VIP</span>
                                        ) : null}
                                    </div>
                                    {showSecondaryName && (
                                        <p className="text-white/60 text-sm mt-0.5">{nomeStr}</p>
                                    )}
                                </div>

                                {/* Ranking + editar — só desktop, alinhado à direita */}
                                <div className="hidden md:flex flex-col items-end gap-1.5 shrink-0 pt-1">
                                    {rankingPosicao && (
                                        <>
                                            <Link to="/ranking" className={rankingBadgeClass}>
                                                Global #{rankingPosicao.posicao_global}
                                            </Link>
                                            {rankingPosicao.posicao_semanal != null && (
                                                <Link to="/ranking" className={rankingBadgeClass}>
                                                    Semanal #{rankingPosicao.posicao_semanal}
                                                </Link>
                                            )}
                                        </>
                                    )}
                                    {onEdit && (
                                        <button
                                            type="button"
                                            onClick={onEdit}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[12px] font-medium text-white/70 hover:bg-orange-500/10 hover:border-orange-500/30 hover:text-orange-400 transition-colors"
                                        >
                                            <Pencil size={13} />
                                            Editar perfil
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Tags */}
                            {user.tags && user.tags.filter((t: any) => t.selecionada !== false).length > 0 && (
                                <div className="mt-2.5 flex flex-wrap items-center justify-center md:justify-start gap-2 md:gap-3">
                                    {user.tags
                                        .filter((t: any) => t.selecionada !== false)
                                        .map((tag: any) => (
                                            <UserTagBadge key={tag.tag_id} tag={tag} />
                                        ))}
                                </div>
                            )}

                            {/* XP bar — mais larga, texto integrado */}
                            <div className="mt-3 w-full max-w-xs mx-auto md:mx-0">
                                <div className="flex justify-between text-[10px] text-white/40 mb-1 font-medium uppercase tracking-wider">
                                    <span>Nível {ownerLevel}</span>
                                    <span className="tabular-nums">{ownerXp.toLocaleString('pt-BR')} XP total</span>
                                </div>
                                <XpProgressBar progress={calculateProgress(ownerXp, ownerLevel)} size="sm" />
                                {xpNeedLevel > 0 && (
                                    <p className="mt-1 flex items-center justify-center md:justify-start gap-0.5 text-[11px] text-white/45 tabular-nums">
                                        {xpInLevel.toLocaleString('pt-BR')} / {xpNeedLevel.toLocaleString('pt-BR')} XP para o próximo nível
                                        <ArrowUpRight size={12} className="text-orange-300/80 shrink-0" aria-hidden />
                                    </p>
                                )}
                            </div>

                            {/* Seguidores · Seguindo · Membro desde (numa linha no desktop) */}
                            <div className="mt-2.5 flex flex-wrap items-center justify-center md:justify-start gap-x-3 gap-y-1 text-sm text-white/55 tabular-nums">
                                <span><span className="font-semibold text-white">{Number(user.seguidores ?? 0)}</span> seguidores</span>
                                <span className="text-white/20">·</span>
                                <span><span className="font-semibold text-white">{Number(user.seguindo ?? 0)}</span> seguindo</span>
                                {memberSinceLabel && (
                                    <>
                                        <span className="hidden md:inline text-white/20">·</span>
                                        <span className="hidden md:inline text-xs text-white/40">{memberSinceLabel}</span>
                                    </>
                                )}
                            </div>
                            {memberSinceLabel && (
                                <p className="mt-1 md:hidden text-xs text-white/45 text-center">{memberSinceLabel}</p>
                            )}

                            {/* Descrição */}
                            {user.usr_descricao && (
                                <div className="mt-3 max-w-lg mx-auto md:mx-0">
                                    <p className="text-white/65 text-sm leading-relaxed whitespace-pre-wrap">{user.usr_descricao}</p>
                                </div>
                            )}

                            {/* Ranking — só mobile */}
                            {rankingPosicao && (
                                <div className="md:hidden mt-3 flex flex-wrap gap-2 justify-center">
                                    <Link to="/ranking" className={rankingBadgeClass}>
                                        Global #{rankingPosicao.posicao_global}
                                    </Link>
                                    {rankingPosicao.posicao_semanal != null && (
                                        <Link to="/ranking" className={rankingBadgeClass}>
                                            Semanal #{rankingPosicao.posicao_semanal}
                                        </Link>
                                    )}
                                </div>
                            )}

                            {/* Editar perfil — só mobile */}
                            {onEdit && (
                                <div className="md:hidden mt-3 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={onEdit}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-[13px] font-medium text-white/70 hover:bg-orange-500/10 hover:border-orange-500/30 hover:text-orange-400 transition-colors"
                                    >
                                        <Pencil size={14} />
                                        Editar perfil
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // PerfilPage layout
    const bannerUrl = getUserBannerUrl(user.usr_id, user.usr_banner);
    const visitorMolTipo = user?.moldura?.mol_tipo;
    const visitorOuterRing = visitorMolTipo === 2 ? 'rounded-xl' : 'rounded-full';
    const visitorAvatarShape = visitorMolTipo === 2 ? 'rounded-lg' : 'rounded-full';

    return (
        <div className="relative">
            {/* Banner */}
            <div className="h-48 md:h-80 w-full relative overflow-hidden">
                {bannerUrl ? (
                    <img
                        src={bannerUrl}
                        alt="Banner"
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-900/40 via-neutral-900 to-neutral-950" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/60 to-transparent" />

                <Link to="/" className="absolute top-6 left-6 p-2 bg-black/40 backdrop-blur-md rounded-full text-white hover:bg-white/10 transition-colors z-10">
                    <ArrowLeft size={24} />
                </Link>
            </div>

            {/* Profile Info */}
            <div className="container mx-auto px-4 md:px-6 relative -mt-20">
                <div className="flex flex-col md:flex-row items-end md:items-end gap-6 md:gap-12 lg:gap-16">
                    {/* Avatar */}
                    <div
                        className={`relative ${visitorOuterRing} p-1.5 bg-neutral-950 inline-block md:block self-center md:self-auto`}
                    >
                        <div className="relative">
                            {user.moldura && (
                                <div className="absolute -inset-1 z-20 pointer-events-none">
                                    <img
                                        src={getMolduraImageUrl(user.moldura.mol_imagem) || ''}
                                        className="w-[100%] h-[100%] max-w-none max-h-none -left-[20%] -top-[20%]"
                                        alt={user.moldura.mol_nome}
                                    />
                                </div>
                            )}
                            <div
                                className={`w-32 h-32 md:w-40 md:h-40 relative ${visitorAvatarShape} overflow-hidden border-4 border-neutral-800 bg-neutral-900 z-10`}
                            >
                                <img
                                    src={getUserImageUrl(user.usr_id, user.usr_imagem)}
                                    alt={user.usr_nome}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                                    }}
                                />
                            </div>
                            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-30">
                                <LevelBadge level={user.usr_nivel || 1} size="md" className="shadow-black/50" />
                            </div>
                        </div>
                    </div>

                    {/* Text Info */}
                    <div className="flex-1 text-center md:text-left mb-2 w-full">
                        <div className="flex flex-col md:flex-row items-center md:items-end gap-4 md:gap-8 justify-between w-full">
                            <div className="min-w-0 flex-1">
                                <h1 className="text-3xl font-bold text-white mb-1 flex flex-col md:flex-row items-center md:items-end gap-2 md:gap-5 justify-center md:justify-start">
                                    {user.usr_nome}
                                    {user.tags &&
                                        user.tags.filter((t: any) => t.selecionada !== false).length > 0 && (
                                        <div className="flex gap-2 md:gap-3.5 mb-1">
                                            {user.tags.filter((t: any) => t.selecionada !== false).map((tag: any) => (
                                                <UserTagBadge key={tag.tag_id} tag={tag} />
                                            ))}
                                        </div>
                                    )}
                                </h1>

                                <p className="text-white/60 text-lg mb-2">@{user.usr_nick}</p>

                                <SeguidoresSeguindoRow
                                    seguidores={Number(user.seguidores ?? 0)}
                                    seguindo={Number(user.seguindo ?? 0)}
                                    className="mb-3"
                                />

                                {rankingPosicao && (
                                    <div className="mb-3 flex flex-wrap gap-2 md:gap-4 justify-center md:justify-start">
                                        <Link
                                            to="/ranking"
                                            className="inline-flex items-center px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[11px] font-semibold text-white/75 hover:text-orange-400 hover:border-orange-500/25 transition-colors"
                                        >
                                            Global #{rankingPosicao.posicao_global}
                                        </Link>
                                        {rankingPosicao.posicao_semanal != null && (
                                            <Link
                                                to="/ranking"
                                                className="inline-flex items-center px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[11px] font-semibold text-white/75 hover:text-orange-400 hover:border-orange-500/25 transition-colors"
                                            >
                                                Semanal #{rankingPosicao.posicao_semanal}
                                            </Link>
                                        )}
                                    </div>
                                )}

                                {user.usr_descricao && (
                                    <div className="mb-4 max-w-lg mx-auto md:mx-0">
                                        <p className="text-white/70 text-sm leading-relaxed italic">
                                            {user.usr_descricao}
                                        </p>
                                    </div>
                                )}

                                {/* XP Bar */}
                                <div className="w-full max-w-[200px] mx-auto md:mx-0">
                                    <div className="flex justify-between text-[10px] text-white/40 mb-1 font-medium uppercase tracking-wider">
                                        <span>Nível {user.usr_nivel || 1}</span>
                                        <span>{user.usr_xp || 0} XP</span>
                                    </div>
                                    <XpProgressBar
                                        progress={calculateProgress(user.usr_xp || 0, user.usr_nivel || 1)}
                                        size="sm"
                                    />
                                </div>
                            </div>

                            {followActions?.canToggle ? (
                                <div className="flex flex-col items-center md:items-end gap-2 shrink-0 w-full md:w-auto pb-1">
                                    <button
                                        type="button"
                                        disabled={followActions.loading}
                                        onClick={() => void followActions.onToggle()}
                                        className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors min-w-[10rem] ${
                                            followActions.isFollowing
                                                ? 'bg-white/10 text-white border border-white/15 hover:bg-white/[0.14]'
                                                : 'bg-orange-500 text-neutral-950 hover:bg-orange-400 border border-orange-400/30'
                                        } disabled:opacity-50 disabled:pointer-events-none`}
                                    >
                                        <UserPlus size={18} className={followActions.isFollowing ? 'opacity-60' : ''} />
                                        {followActions.loading
                                            ? '…'
                                            : followActions.isFollowing
                                              ? 'Seguindo'
                                              : 'Seguir'}
                                    </button>
                                </div>
                            ) : null}
                        </div>

                        {/* Desktop Stats */}
                        {stats && (
                            <div className="hidden md:flex items-center gap-12 lg:gap-14 border-t border-white/5 pt-4 mt-4">
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-2xl font-bold text-white">{stats.total_leituras}</span>
                                    <span className="text-xs text-white/40 uppercase tracking-widest font-medium">Na Lista</span>
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-2xl font-bold text-white">{stats.total_lidos}</span>
                                    <span className="text-xs text-white/40 uppercase tracking-widest font-medium">Lidos</span>
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-2xl font-bold text-white">{stats.total_favoritos}</span>
                                    <span className="text-xs text-white/40 uppercase tracking-widest font-medium">Favoritos</span>
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-2xl font-bold text-white">{stats.total_comentarios}</span>
                                    <span className="text-xs text-white/40 uppercase tracking-widest font-medium">Comentários</span>
                                </div>
                                <div className="flex-1 text-right text-white/30 text-xs flex items-center justify-end gap-2">
                                    <Calendar size={14} />
                                    Entrou em {dayjs(user.usr_criado_em).format('MMM YYYY')}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile Stats */}
                {stats && (
                    <div className="grid grid-cols-2 gap-2 mt-6 md:hidden">
                        <div className="bg-neutral-900/50 rounded-lg p-3 text-center border border-white/5">
                            <span className="block text-xl font-bold text-white mb-1">{stats.total_leituras}</span>
                            <span className="text-[10px] text-white/40 uppercase font-medium">Lista</span>
                        </div>
                        <div className="bg-neutral-900/50 rounded-lg p-3 text-center border border-white/5">
                            <span className="block text-xl font-bold text-white mb-1">{stats.total_lidos}</span>
                            <span className="text-[10px] text-white/40 uppercase font-medium">Lidos</span>
                        </div>
                        <div className="bg-neutral-900/50 rounded-lg p-3 text-center border border-white/5">
                            <span className="block text-xl font-bold text-white mb-1">{stats.total_favoritos}</span>
                            <span className="text-[10px] text-white/40 uppercase font-medium">Favs</span>
                        </div>
                        <div className="bg-neutral-900/50 rounded-lg p-3 text-center border border-white/5">
                            <span className="block text-xl font-bold text-white mb-1">{stats.total_comentarios}</span>
                            <span className="text-[10px] text-white/40 uppercase font-medium">Msg</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
